import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Pull-to-refresh wrapper for mobile.
 * Wrap any list/page; pulls down past 70px from the top → fires onRefresh().
 * Desktop: passes through (no gesture handling) so this is mobile-first.
 *
 * Usage:
 *   <PullToRefresh onRefresh={loadData}>
 *     ...page content...
 *   </PullToRefresh>
 */
export default function PullToRefresh({ onRefresh, children, threshold = 70, disabled = false }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (disabled) return;
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (refreshing) return;
      // Only start when at the very top of the page
      if (window.scrollY > 0) {
        dragging.current = false;
        return;
      }
      startY.current = e.touches[0].clientY;
      dragging.current = true;
    };

    const onTouchMove = (e) => {
      if (!dragging.current || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && window.scrollY === 0) {
        // dampen the pull (rubber band effect)
        const damped = Math.min(dy * 0.5, threshold * 1.6);
        setPull(damped);
      } else if (dy < 0) {
        dragging.current = false;
        setPull(0);
      }
    };

    const onTouchEnd = async () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (pull >= threshold) {
        setRefreshing(true);
        setPull(threshold);
        try {
          await Promise.resolve(onRefresh?.());
        } catch {
          // swallow — caller toasts
        } finally {
          // brief hold so user sees the spinner, then collapse
          setTimeout(() => {
            setRefreshing(false);
            setPull(0);
          }, 350);
        }
      } else {
        setPull(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [onRefresh, pull, refreshing, threshold, disabled]);

  const progress = Math.min(pull / threshold, 1);
  const showIndicator = pull > 0 || refreshing;

  return (
    <div ref={containerRef} className="relative" data-testid="pull-to-refresh">
      {/* Indicator */}
      <div
        aria-hidden={!showIndicator}
        className="absolute left-1/2 -translate-x-1/2 z-20 flex items-center justify-center pointer-events-none"
        style={{
          top: showIndicator ? Math.max(pull - 36, 8) : -40,
          opacity: refreshing ? 1 : progress,
          transition: refreshing ? 'opacity 0.2s ease' : 'none',
        }}
      >
        <div className="bg-white rounded-full shadow-md w-10 h-10 flex items-center justify-center border border-[#f3f3f3]">
          <RefreshCw
            size={18}
            className={`text-[#d92027] ${refreshing ? 'animate-spin' : ''}`}
            style={{ transform: refreshing ? 'none' : `rotate(${progress * 270}deg)` }}
          />
        </div>
      </div>

      {/* Content — translate down while pulling */}
      <div
        style={{
          transform: pull > 0 || refreshing ? `translateY(${pull}px)` : 'none',
          transition: dragging.current ? 'none' : 'transform 0.25s ease',
        }}
      >
        {children}
      </div>
    </div>
  );
}
